/**
 * One post on the wall.
 *
 * Presentational: it renders what it is handed and reports intent upwards. All
 * fetching, permission resolution and optimistic bookkeeping stay in WallPage,
 * so this stays cheap to reason about and to restyle.
 */

import { useState } from "react";
import { Avatar } from "../../components/ui/Avatar.js";
import { Button } from "../../components/ui/Button.js";
import { relativeTime, exactTime } from "../../lib/relativeTime.js";
import { PostMedia } from "./PostMedia.js";
import { DISLIKE, LIKE, type Comment, type Post } from "./types.js";
import type { MediaRef } from "../../lib/useDecryptedUrl.js";
import "./PostCard.css";

export interface PostCardProps {
  post: Post;
  /** Signed thumbnail URLs, keyed by file id. Missing ones render as blanks. */
  thumbRefs: Record<string, MediaRef>;
  groupId: string;
  comments: Comment[] | undefined;
  commentsOpen: boolean;
  canComment: boolean;
  canReact: boolean;
  canDelete: boolean;
  isDeleting: boolean;
  onToggleComments: () => void;
  onReact: (emoji: string) => void;
  onComment: (body: string) => void;
  onDelete: () => void;
  onOpenMedia: (fileId: string) => void;
}

export function PostCard({
  post,
  thumbRefs,
  groupId,
  comments,
  commentsOpen,
  canComment,
  canReact,
  canDelete,
  isDeleting,
  onToggleComments,
  onReact,
  onComment,
  onDelete,
  onOpenMedia,
}: PostCardProps) {
  const [draft, setDraft] = useState("");

  /**
   * The last two comments, shown without asking.
   *
   * Taken from the end because a thread reads oldest-first and the newest
   * remarks are the ones worth surfacing — the same choice every feed makes.
   * Only what has already been fetched: this never triggers a load of its own,
   * so a wall of fifty posts still costs fifty rows and no extra requests.
   */
  const preview: Comment[] = comments
    ? comments.slice(-2)
    : post.recentComments;

  const tally = (emoji: string) =>
    post.reactions.find((r) => r.emoji === emoji)?.count ?? 0;
  const mine = (emoji: string) => post.viewerReactions.includes(emoji);
  const authorName = post.author?.displayName ?? "Someone who left";

  const submitComment = () => {
    const body = draft.trim();
    if (body.length === 0) return;
    onComment(body);
    setDraft("");
  };

  return (
    <article className="fc-post">
      <header className="fc-post__head">
        <Avatar name={authorName} size="md" decorative />
        <div className="fc-post__byline">
          <span className="fc-post__author">{authorName}</span>
          <time
            className="fc-post__time"
            dateTime={post.createdAt}
            title={exactTime(post.createdAt)}
          >
            {relativeTime(post.createdAt)}
          </time>
        </div>

        {canDelete && (
          <button
            type="button"
            className="fc-post__delete"
            aria-label="Delete post"
            disabled={isDeleting}
            onClick={onDelete}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
            </svg>
          </button>
        )}
      </header>

      {/*
        Photos come before the words, and bleed to the card's edges.
        That ordering is what makes a feed scannable: the image is what someone
        stops for, and the caption is what they read once they have.
      */}
      {post.fileIds.length > 0 && (
        <PostMedia
          fileIds={post.fileIds}
          sources={thumbRefs}
          groupId={groupId}
          onOpen={onOpenMedia}
        />
      )}

      {/*
        Drawn rather than typed.

        These were the 👍 and 👎 characters, which render as a different picture
        on every platform — full colour on one, a flat outline on another, and
        always at the mercy of a font. A pair of strokes that match the rest of
        the interface, fill when they are yours, and can be the accent colour
        when set is both steadier and quieter.
      */}
      <footer className="fc-post__actions">
        <button
          type="button"
          className="fc-post__reaction"
          data-active={mine(LIKE) || undefined}
          aria-pressed={mine(LIKE)}
          aria-label={mine(LIKE) ? "Remove your like" : "Like"}
          disabled={!canReact}
          onClick={() => onReact(LIKE)}
        >
          <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
            data-filled={mine(LIKE) || undefined}
          >
            <path d="M7 10.5V20H4.5A1.5 1.5 0 0 1 3 18.5v-6.5A1.5 1.5 0 0 1 4.5 10.5zM7 10.5l4.2-7.1a1 1 0 0 1 1.8.5v4.1h4.8a2 2 0 0 1 1.96 2.4l-1.3 6.5A2 2 0 0 1 16.5 20H7z" />
          </svg>
          {tally(LIKE) > 0 && <span className="fc-tabular">{tally(LIKE)}</span>}
        </button>

        <button
          type="button"
          className="fc-post__reaction"
          data-active={mine(DISLIKE) || undefined}
          aria-pressed={mine(DISLIKE)}
          aria-label={mine(DISLIKE) ? "Remove your dislike" : "Dislike"}
          disabled={!canReact}
          onClick={() => onReact(DISLIKE)}
        >
          <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
            data-filled={mine(DISLIKE) || undefined}
          >
            <path d="M17 13.5V4h2.5A1.5 1.5 0 0 1 21 5.5V12a1.5 1.5 0 0 1-1.5 1.5zM17 13.5l-4.2 7.1a1 1 0 0 1-1.8-.5v-4.1H6.2a2 2 0 0 1-1.96-2.4l1.3-6.5A2 2 0 0 1 7.5 4H17z" />
          </svg>
          {tally(DISLIKE) > 0 && (
            <span className="fc-tabular">{tally(DISLIKE)}</span>
          )}
        </button>

        <button
          type="button"
          className="fc-post__reaction fc-post__comments-toggle"
          aria-expanded={commentsOpen}
          aria-label="Comments"
          onClick={onToggleComments}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M21 12a8 8 0 0 1-8 8H7l-4 3V12a8 8 0 0 1 8-8h2a8 8 0 0 1 8 8z" />
          </svg>
          {post.commentCount > 0 && (
            <span className="fc-tabular">{post.commentCount}</span>
          )}
        </button>
      </footer>

      {/* Caption reads as speech: who said it, then what they said. */}
      {post.body && (
        <p className="fc-post__body">
          <span className="fc-post__body-author">{authorName}</span> {post.body}
        </p>
      )}

      {/*
        The conversation is part of the post, not a drawer behind it.

        A collapsed thread said only how many replies were waiting, so the
        liveliest thing about a post — what people actually said — was a click
        away and looked like a footnote. The most recent two are shown as soon
        as they load, and the rest stay one tap away, because a thread of forty
        would otherwise bury the next post.
      */}
      {!commentsOpen && preview.length > 0 && (
        <ul className="fc-post__comments fc-post__comments--preview">
          {preview.map((comment) => (
            <li key={comment.id} className="fc-comment fc-comment--tight">
              <span className="fc-comment__author">
                {comment.author?.displayName ?? "Someone who left"}
              </span>{" "}
              <span className="fc-comment__text">{comment.body}</span>
            </li>
          ))}
        </ul>
      )}

      {!commentsOpen && post.commentCount > preview.length && (
        <button
          type="button"
          className="fc-post__thread-peek"
          onClick={onToggleComments}
        >
          {post.commentCount === 1
            ? "View 1 comment"
            : `View all ${String(post.commentCount)} comments`}
        </button>
      )}

      {/* With nothing said yet, the invitation is the affordance. */}
      {!commentsOpen && post.commentCount === 0 && canComment && (
        <button
          type="button"
          className="fc-post__thread-peek"
          onClick={onToggleComments}
        >
          Add a comment
        </button>
      )}

      {commentsOpen && (
        <section className="fc-post__thread" aria-label="Comments">
          {comments === undefined ? (
            <p className="fc-post__thread-empty">Loading…</p>
          ) : comments.length === 0 ? (
            <p className="fc-post__thread-empty">No comments yet.</p>
          ) : (
            <ul className="fc-post__comments">
              {comments.map((comment) => (
                <li key={comment.id} className="fc-comment">
                  <Avatar
                    name={comment.author?.displayName ?? "?"}
                    size="sm"
                    decorative
                  />
                  <div className="fc-comment__body">
                    <span className="fc-comment__author">
                      {comment.author?.displayName ?? "Someone who left"}
                    </span>
                    <span className="fc-comment__text">{comment.body}</span>
                    <time
                      className="fc-comment__time"
                      dateTime={comment.createdAt}
                      title={exactTime(comment.createdAt)}
                    >
                      {relativeTime(comment.createdAt)}
                    </time>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {canComment && (
            <div className="fc-post__reply">
              <textarea
                className="fc-post__reply-input"
                rows={1}
                value={draft}
                placeholder="Write a comment…"
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  // Enter sends, Shift+Enter breaks the line — the convention
                  // every chat box has trained people to expect.
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    submitComment();
                  }
                }}
              />
              <Button
                variant="secondary"
                size="sm"
                disabled={draft.trim().length === 0}
                onClick={submitComment}
              >
                Send
              </Button>
            </div>
          )}
        </section>
      )}
    </article>
  );
}
